/**
 * TrendChart Component
 * Reusable Chart.js line chart for Scaling Up trend metrics.
 * Highlights the Olympic Best data point with a distinct marker.
 * Supports click-to-drill-down and event indicator dots.
 */

import { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import type { ChartOptions, ChartEvent, ActiveElement } from 'chart.js';
import type { Chart as ChartJS } from 'chart.js';
import { CommentButton } from './CommentButton';
import { formatCurrency } from '@/utils/formatters';

interface TrendChartProps {
  title: string;
  labels: string[];
  values: number[];
  olympicBestIndex: number | null;
  eventIndices?: number[];
  onBarClick?: (index: number) => void;
  onAIInsight?: () => void;
  insightLoading?: boolean;
  formatValue?: (value: number) => string;
  unit?: string;
  commentTargetId?: string;
}

const MARINE = '#075156';
const CITRUS = '#ABDD65';
const NAVY = '#17232D';
const EVENT_DOT = '#ABDD65';

export function TrendChart({
  title,
  labels,
  values,
  olympicBestIndex,
  eventIndices = [],
  onBarClick,
  onAIInsight,
  insightLoading = false,
  formatValue,
  unit,
  commentTargetId,
}: TrendChartProps) {
  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const format = formatValue ?? defaultFormat;

  const pointBackgroundColors = values.map((_, i) =>
    i === olympicBestIndex ? CITRUS : MARINE
  );
  const pointRadii = values.map((_, i) =>
    i === olympicBestIndex ? 8 : 4
  );
  const pointBorderWidths = values.map((_, i) =>
    i === olympicBestIndex ? 3 : 2
  );

  const chartData = {
    labels,
    datasets: [
      {
        label: title,
        data: values,
        borderColor: MARINE,
        backgroundColor: 'rgba(7, 81, 86, 0.08)',
        borderWidth: 2.5,
        fill: true,
        tension: 0.3,
        pointRadius: pointRadii,
        pointHoverRadius: 8,
        pointBackgroundColor: pointBackgroundColors,
        pointBorderColor: '#ffffff',
        pointBorderWidth: pointBorderWidths,
      },
    ],
  };

  const handleChartClick = (
    _event: ChartEvent,
    elements: ActiveElement[]
  ) => {
    if (!onBarClick || elements.length === 0) return;
    const idx = elements[0].index;
    onBarClick(idx);
  };

  const cursor = onBarClick ? 'pointer' : 'default';

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2.2,
    animation: false,
    onClick: handleChartClick,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: NAVY,
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 10,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          label: (ctx) => format(Number(ctx.parsed.y)),
          afterLabel: (ctx) => {
            if (eventIndices.includes(ctx.dataIndex)) return '● has context event';
            return '';
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          callback: (value) => formatTick(Number(value), unit),
          color: '#6b7280',
          font: { family: 'Montserrat, sans-serif', size: 11 },
          maxTicksLimit: 5,
        },
        grid: { color: 'rgba(229, 238, 239, 0.5)' },
      },
      x: {
        ticks: {
          color: '#374151',
          font: { family: 'Montserrat, sans-serif', size: 11 },
        },
        grid: { display: false },
      },
    },
  };

  const olympicLabel = olympicBestIndex !== null
    ? `${labels[olympicBestIndex]}: ${format(values[olympicBestIndex])}`
    : null;

  return (
    <div
      className={`trend-chart${onBarClick ? ' trend-chart--clickable' : ''}`}
      style={{ cursor }}
    >
      <div className="trend-chart__header">
        <h3 className="trend-chart__title">{title}</h3>
        {olympicLabel && (
          <span className="trend-chart__olympic" title="Olympic Best">
            &#9733; {olympicLabel}
          </span>
        )}
        {eventIndices.length > 0 && (
          <span
            className="trend-chart__events-badge"
            title={`${eventIndices.length} month(s) have context events`}
            style={{ color: EVENT_DOT }}
          >
            ● {eventIndices.length} event{eventIndices.length !== 1 ? 's' : ''}
          </span>
        )}
        {onAIInsight && (
          <button
            className={`trend-chart__ai-btn${insightLoading ? ' trend-chart__ai-btn--loading' : ''}`}
            onClick={(e) => { e.stopPropagation(); onAIInsight(); }}
            title="AI Insights — what this metric means and current analysis"
            aria-label={`AI Insights for ${title}`}
            disabled={insightLoading}
          >
            {insightLoading ? '…' : 'AI'}
          </button>
        )}
        {commentTargetId && (
          <span onClick={(e) => e.stopPropagation()} role="presentation">
            <CommentButton targetType="trend-chart" targetId={commentTargetId} />
          </span>
        )}
      </div>
      <Line
        ref={chartRef}
        data={chartData}
        options={options}
        aria-label={`${title} trend${onBarClick ? '. Click a point for details.' : ''}`}
      />
      {onBarClick && (
        <p className="trend-chart__hint">Click a data point to drill down</p>
      )}
    </div>
  );
}

function defaultFormat(value: number): string {
  return formatCurrency(value);
}

function formatTick(value: number, unit?: string): string {
  if (unit === '%') return `${value}%`;
  if (unit === 'days') return `${value}d`;
  if (unit === 'months') return `${value}mo`;
  if (unit === 'ratio') return `${value}x`;
  if (value >= 1000) return `$${Math.round(value / 1000)}k`;
  return `$${value}`;
}
